import type { Placement } from "@floating-ui/react"
import { floating, radiusCva, shadowCva, useProvider } from "@overmap-ai/blocks"
import { BubbleMenu as TipTapBubbleMenu } from "@tiptap/react/menus"
import { cx } from "class-variance-authority"
import { type ComponentProps, memo, useMemo } from "react"

import type { OptionalEditorProps } from "../../components"
import { useFallbackEditor } from "../../hooks"
import { BUBBLE_MENU_CONTENT_PADDING, BUBBLE_MENU_MINIMUM_MAX_HEIGHT, BUBBLE_MENU_OVERFLOW_PADDING } from "./constants"
import type { BubbleMenuAlignment, BubbleMenuSide } from "./typings"

type TipTapBubbleMenuProps = ComponentProps<typeof TipTapBubbleMenu>

interface BubbleMenuProps extends Omit<TipTapBubbleMenuProps, "appendTo" | "options" | "editor">, OptionalEditorProps {
	offset?: number
	side?: BubbleMenuSide
	align?: BubbleMenuAlignment
}

export const BubbleMenu = memo<BubbleMenuProps>((props) => {
	const {
		ref,
		children,
		className,
		side = "top",
		align = "center",
		offset = 5,
		// TipTapFloatingMenu props
		editor: editorFromProps,
		...rest
	} = props

	const { radius } = useProvider()
	const editor = useFallbackEditor(editorFromProps)

	const tipTapBubbleMenuOptions = useMemo<TipTapBubbleMenuProps["options"]>(
		() => ({
			strategy: "absolute",
			// TODO: probably some way with template string types to not need the "as Placement"
			placement: `${side}${align !== "center" ? "-" : ""}${align !== "center" ? align : ""}` as Placement,
			offset: { mainAxis: offset },
			size: {
				apply({ availableHeight, elements }) {
					elements.floating.style.setProperty(
						"--overmap-menu-available-height",
						`${Math.max(availableHeight, BUBBLE_MENU_MINIMUM_MAX_HEIGHT)}px`,
					)
				},
				padding: BUBBLE_MENU_OVERFLOW_PADDING,
			},
			flip: { fallbackStrategy: "initialPlacement" },
			shift: { padding: BUBBLE_MENU_CONTENT_PADDING },
			hide: {
				strategy: "escaped",
			},
		}),
		[align, offset, side],
	)

	return (
		<TipTapBubbleMenu
			ref={ref}
			className={cx(
				[
					"bg-(--color-background)",
					"dark:bg-(--base-2)",
					"ring-1",
					"ring-(--base-6)",
					"overflow-hidden",
					"relative",
					"max-h-(--overmap-menu-available-height)",
				],
				radiusCva({ radius, maxLarge: true }),
				shadowCva({ size: "md" }),
				floating({ side }),
				className,
			)}
			// TipTapBubbleMenu props
			editor={editor}
			appendTo={document.body}
			options={tipTapBubbleMenuOptions}
			data-floating-content=""
			{...rest}
		>
			{children}
		</TipTapBubbleMenu>
	)
})
BubbleMenu.displayName = "FloatingMenuRoot"
